import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data.access_token, res.data.user);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary grain grid place-items-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-card rounded p-8 sm:p-10 relative z-10"
      >
        <div className="flex items-center gap-2 mb-8">
          <span className="grid place-items-center w-9 h-9 rounded bg-primary text-primary-foreground">
            <Sprout className="w-5 h-5" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-lg">Owner Login</span>
        </div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground text-sm mt-1">Sign in to manage your bookings and availability.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Email</Label>
            <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@helpmowing.com" className="mt-2" required />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em]">Password</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-2" required />
          </div>
          {error && <p data-testid="admin-login-error" className="text-destructive text-sm">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-50"
          >
            <Lock className="w-4 h-4" /> {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
